/**
 * SmartTable density — persisted in localStorage so a user keeps their
 * preferred row spacing across sessions and across pages.
 */

import { useCallback, useEffect, useState } from 'react';
import type { Density } from './types';

const STORAGE_KEY = 'klaris.smartTable.density';

function readDensity(): Density {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v === 'compact' || v === 'standard' || v === 'comfortable') return v;
  } catch {
    // ignore
  }
  return 'compact';
}

function writeDensity(d: Density) {
  try {
    window.localStorage.setItem(STORAGE_KEY, d);
  } catch {
    // ignore
  }
}

/**
 * Hook that returns the current density and a setter that persists.
 * `initial` lets the caller force a starting density (e.g. dashboards
 * always start compact); user-toggle still wins after that.
 */
export function useDensity(initial?: Density) {
  const [density, setDensity] = useState<Density>(() => initial || readDensity());

  useEffect(() => {
    if (initial) setDensity(initial);
    // initial only applies on mount — subsequent re-renders should respect user choice
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = useCallback((d: Density) => {
    setDensity(d);
    writeDensity(d);
  }, []);

  return [density, update] as const;
}

/** Row pixel height per density. Header/footer use the compact pair always. */
export function rowHeightFor(density: Density): number {
  switch (density) {
    case 'comfortable': return 52;
    case 'standard': return 42;
    case 'compact':
    default: return 34;
  }
}
